import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { Product, Result, FilterField } from 'shared/types/product';
import { Availability } from 'shared/types/product/InventoryQuery';
import { getCurrentCSTHour } from '@/components/customercenter-ui/components/utils/formatUtils';
import { callActionMiddleware } from '@/helpers/utils/callActionHelper';
import {
  GetProductAction,
  GetSearchableProductAttributesAction,
  ProductQueryAction,
  QueryProductCategoriesAction,
  ProductGroupsAction,
  GetEstimatedDeliveryDateAction,
  GetAvailabilityAction,
  UpdateProductRules,
  WarrantyProductAction,
} from '../../types/actions/ProductActions';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { GetEstimatedDeliveryDatePayload } from '../../types/payloads/GetEstimatedDeliveryDatePayload';
import { AvailabilityPayload, WarrantyProductDataPayload } from '../../types/payloads/productPayloads';
import { GetProductQuery, ProductQueryQuery, QueryProductCategoriesQuery } from '../../types/queries/ProductQueries';

export type ProductActions = {
  getProduct: GetProductAction;
  query: ProductQueryAction;
  queryCategories: QueryProductCategoriesAction;
  getSearchableAttributes: GetSearchableProductAttributesAction;
  getProductGroups: ProductGroupsAction;
  getEstimatedDeliveryDate: GetEstimatedDeliveryDateAction;
  getAvailability: GetAvailabilityAction;
  updateProductRules: UpdateProductRules;
  getWarrantyProducts: WarrantyProductAction;
};

export const getProductActions = (sdk: SDK<ComposableCommerceEvents>): ProductActions => {
  return {
    getProduct: async (query: GetProductQuery, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Product>({
        actionName: 'product/getProduct',
        query,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false && response.data) {
        sdk.trigger(
          new Event({
            eventName: 'productFetched',
            data: {
              product: response.data,
            },
          }),
        );
      }
      return response;
    },
    query: async (query: ProductQueryQuery, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Result>({
        actionName: 'product/query',
        query,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productsQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    queryCategories: async (query: QueryProductCategoriesQuery, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Result>({
        actionName: 'product/queryCategories',
        query,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productCategoriesQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getSearchableAttributes: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<FilterField[]>({
        actionName: 'product/searchableAttributes',
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'searchableProductAttributesFetched',
            data: {
              filterFields: response.data,
            },
          }),
        );
      }
      return response;
    },
    getProductGroups: async (payload: any, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await callActionMiddleware<Response>({
        actionName: 'product/getProductGroups',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    getEstimatedDeliveryDate: async (
      payload: GetEstimatedDeliveryDatePayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const currentCSTHour = getCurrentCSTHour();
      payload.hour = currentCSTHour?.toString();
      const response = await callActionMiddleware<Response>({
        actionName: 'fedex/getDeliveryDate',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    getAvailability: async (payload: AvailabilityPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Availability>({
        actionName: 'product/getAvailability',
        payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },

    updateProductRules: async (payload: any, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Response>({
        actionName: 'product/updateProductRules',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getWarrantyProducts: async (
      payload: WarrantyProductDataPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Response>({
        actionName: 'product/getWarrantyProducts',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
  };
};
